import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { LanguageCode } from '../../types';
import { LanguageContext } from '../context/language';
import { useUser } from '../context/user';
import { getLanguageCode } from '../languageCodeFormater';
import useUserSettings from '../use-user-settings';

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { user } = useUser();
  const { updateUser } = useUserSettings(undefined, true);

  useEffect(() => {
    const changeLanguageIfNeeded = async () => {
      if (
        location.pathname !== '/user-info' &&
        location.pathname !== '/company-info' &&
        location.pathname !== '/add-facility' &&
        location.pathname !== '/onboarding-start' &&
        location.pathname !== '/set-password' &&
        user
      ) {
        if (user.language && getLanguageCode(i18n.language) !== user.language) {
          await i18n.changeLanguage(user.language as LanguageCode);
        }
      }
    };

    changeLanguageIfNeeded();
  }, [location.pathname, user, i18n]);

  const setLanguage = useCallback(
    async (language: LanguageCode): Promise<TFunction> => {
      if (user) {
        await updateUser({ language });
      }
      return i18n.changeLanguage(language as string);
    },
    [i18n, updateUser, user]
  );

  const contextValue = useMemo(
    () => ({
      language: getLanguageCode(i18n.language),
      setLanguage,
    }),
    [i18n.language, setLanguage]
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
